import React from "react";
import TextInput from "components/TextInput";
import { getCurrentReferenceValue } from "features/MappingTools/utils";
import { useTranslation } from "react-i18next";
import { componentsStore } from "store/Mapping/ComponentsStore";
import { observer } from "mobx-react-lite";
import Input from "components/Input";
import ComponentChildContent from "../../ComponentChildOverview/ComponentChildContent";
import { ComponentDetailsByFileOut } from "api-client";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  IconArrowDown,
  IconArrowUp,
  IconGripVertical,
} from "@tabler/icons-react";
import clsx from "clsx";

const AddedChild = ({
  childItem,
  rowIndex,
  ...props
}: {
  childItem: ComponentDetailsByFileOut;
  rowIndex: number;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  sortableId?: string;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const itemReferenceUnit = getCurrentReferenceValue(childItem.reference_unit);

  const onChangeValue = (
    key: keyof ComponentDetailsByFileOut,
    value?: string
  ) => {
    componentsStore.onChangeChildParams(key, rowIndex, value);
  };

  const id = String(props.sortableId);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
  };

  const isFirst = rowIndex === 0;
  const isLast = rowIndex === componentsStore.data.childrenList.length - 1;

  const reorderOptions = (
    <div className="flex items-center transition">
      <IconArrowUp
        className={clsx(
          "text-indigo-700 hover:text-indigo-500 w-6 h-6 mr-1 cursor-pointer",
          {
            "cursor-not-allowed pointer-events-none opacity-50": isFirst,
          }
        )}
        onClick={isFirst ? undefined : () => props.moveUp(rowIndex)}
      />
      <IconArrowDown
        className={clsx(
          "text-indigo-700 hover:text-indigo-500 w-6 h-6 mr-1 cursor-pointer",
          {
            "cursor-not-allowed pointer-events-none opacity-50": isLast,
          }
        )}
        onClick={isLast ? undefined : () => props.moveDown(rowIndex)}
      />
    </div>
  );

  const dragHandle = (
    <IconGripVertical
      size={20}
      className="mr-2 min-w-[20px] cursor-grab focus:outline-none text-indigo-700 hover:text-indigo-500"
      {...listeners}
      {...attributes}
    />
  );

  return (
    <div
      className="w-full bg-white border border-gray-300 rounded-md overflow-hidden mb-3"
      style={style}
      ref={setNodeRef}
    >
      <ComponentChildContent
        childItem={childItem}
        removable
        reorderOptions={reorderOptions}
        dragHandle={dragHandle}
        className="px-4 relative"
        moveUp={rowIndex === 0 ? undefined : () => props.moveUp(rowIndex)}
        moveDown={
          rowIndex === componentsStore.data.childrenList.length - 1
            ? undefined
            : () => props.moveDown(rowIndex)
        }
      />
      <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-between gap-4 min-w-[200px]">
        <TextInput
          unit={`${
            isEng
              ? itemReferenceUnit?.name ?? "-"
              : itemReferenceUnit?.name_de ?? "-"
          }/${componentsStore.getValueFromObject("reference_unit", isEng)}`}
          noMargin
          labelName={`${t("components.unitConversion")}*`}
          handleChange={(value) => onChangeValue("unit_conversion", value)}
          value={childItem.unit_conversion as unknown as string}
          type="number"
          min={0}
          hasError={
            Number(childItem.unit_conversion) === 0 ||
            childItem.unit_conversion === undefined
          }
        />
        <Input
          placeholder={t("components.addComment")}
          labelName={t("components.comment")}
          noMargin
          containerClassName="w-full"
          onChange={(value) => onChangeValue("comment", value)}
          value={childItem.comment ?? ""}
        />
      </div>
    </div>
  );
};

export default observer(AddedChild);
